import dayjs from "dayjs";

import type { BacklinksBacklink } from "@similarweb/pro-api-client";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { shortMonthDayAndShortYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { PlainTooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import type { Translate } from "app/filters/i18nFilter";
import { UrlWithTitleCell } from "app/components/React/Table/cells";
import { AnchorCell } from "app/components/React/Table/cells/AnchorCell/AnchorCell";
import { NewLostCell } from "app/components/React/Table/cells/NewLostCell";

import { Truncate } from "../../../shared/BacklinksStyledComponents";
import { IndexCell, numFilter } from "../../../shared/BacklinksUtils";

type GetBacklinksTableColumnsProps = {
    sortField: string;
    isSortAsc: boolean;
    translate: Translate;
    rowsCount?: number;
    currentPage?: number;
};

export const getBacklinksTableColumns = ({
    isSortAsc,
    sortField,
    translate,
    rowsCount = 0,
    currentPage = 1,
}: GetBacklinksTableColumnsProps): ColumnsType<BacklinksBacklink> => {
    return [
        {
            width: 50,
            render: IndexCell(currentPage),
            // Index never changes as the data comes in single page batches
            // And because we cache the results the loading state does not clears out previous rendered cells
            // Therefore we need to make sure this cell is always updated
            shouldCellUpdate: () => true,
            align: "center",
        },
        {
            fixed: true,
            width: 370,
            render: (value, row) => (
                <UrlWithTitleCell
                    value={value}
                    row={row}
                />
            ),
            title: `${translate("digitalsuite.backlinks.table.column.url_from")} (${numFilter(rowsCount)})`,
            tooltipTitle: `${translate("digitalsuite.backlinks.table.column.url_from")} (${numFilter(rowsCount)})`,
            dataIndex: "UrlFrom",
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.url_from.tooltip"),
        },
        {
            width: 120,
            render: (value, row) => (
                <AnchorCell
                    value={value}
                    row={{ ...row, Image: !!row.ImageSource }}
                />
            ),
            title: translate("digitalsuite.backlinks.table.column.anchor"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.anchor"),
            dataIndex: "Anchor",
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.anchor.tooltip"),
        },
        {
            width: 150,
            align: "right",
            title: translate("digitalsuite.backlinks.table.column.influence"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.influence"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.influence.description"),
            dataIndex: "Rank",
            sorter: true,
            sortOrder: sortField === "Rank" ? (isSortAsc ? "ascend" : "descend") : undefined,
        },
        {
            width: 120,
            align: "right",
            title: translate("digitalsuite.backlinks.table.column.domain_score"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.domain_score"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.domain_score.description"),
            dataIndex: "DomainScore",
            sorter: true,
            sortOrder: sortField === "DomainScore" ? (isSortAsc ? "ascend" : "descend") : undefined,
        },
        {
            width: 280,
            render: (value, row) => {
                let newLostComp = null;
                if (row.IsNew) {
                    newLostComp = <NewLostCell isNew={true} />;
                } else if (row.IsLost) {
                    newLostComp = <NewLostCell isNew={false} />;
                }

                return (
                    <Flex
                        width="100%"
                        gap="8px"
                        alignItems="center"
                        overflow="hidden"
                    >
                        <PlainTooltip tooltipContent={value as string}>
                            <Truncate>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {value as string}
                                </Text>
                            </Truncate>
                        </PlainTooltip>
                        {newLostComp}
                    </Flex>
                );
            },
            title: translate("digitalsuite.backlinks.table.column.url_to"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.url_to"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.url_to.tooltip"),
            dataIndex: "UrlTo",
        },
        {
            width: 100,
            align: "right",
            render: (value) => dayjs(value as string).format(shortMonthDayAndShortYear),
            title: translate("digitalsuite.backlinks.table.column.first_seen"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.first_seen"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.first_seen.tooltip"),
            dataIndex: "FirstSeen",
            sorter: true,
            sortOrder: sortField === "FirstSeen" ? (isSortAsc ? "ascend" : "descend") : undefined,
        },
        {
            width: 100,
            align: "right",
            render: (value) => dayjs(value as string).format(shortMonthDayAndShortYear),
            title: translate("digitalsuite.backlinks.table.column.last_visited"),
            tooltipTitle: translate("digitalsuite.backlinks.table.column.last_visited"),
            tooltipSubtitle: translate("digitalsuite.backlinks.table.column.last_visited.tooltip"),
            dataIndex: "LastVisited",
            sorter: true,
            sortOrder: sortField === "LastVisited" ? (isSortAsc ? "ascend" : "descend") : undefined,
        },
    ];
};
